import React from 'react';
import { Container, Row, Col, Card, Button, Alert, Form, Badge, Table } from 'react-bootstrap';
import './DesignSystem.css';

const DesignSystem: React.FC = () => {
  return (
    <div className="design-system">
      <Container fluid className="py-4">
        <h1 className="design-system-title mb-5">Design System</h1>

        <section className="mb-5">
          <h2 className="section-title mb-4">Colors</h2>

          <h3 className="subsection-title mb-3">Primary Colors</h3>
          <Row className="mb-4">
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-primary-500)' }}></div>
                <div className="color-info">
                  <p className="color-name">Primary 500</p>
                  <p className="color-value">--color-primary-500</p>
                  <p className="color-hex">#DDEB4A</p>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-primary-900)' }}></div>
                <div className="color-info">
                  <p className="color-name">Primary 900</p>
                  <p className="color-value">--color-primary-900</p>
                  <p className="color-hex">#474920</p>
                </div>
              </div>
            </Col>
          </Row>

          <h3 className="subsection-title mb-3">Text Colors</h3>
          <Row className="mb-4">
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-text-body)' }}></div>
                <div className="color-info">
                  <p className="color-name">Text Body</p>
                  <p className="color-value">--color-text-body</p>
                  <p className="color-hex">#191919</p>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-text-primary)' }}></div>
                <div className="color-info">
                  <p className="color-name">Text Primary</p>
                  <p className="color-value">--color-text-primary</p>
                  <p className="color-hex">#121217</p>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-text-secondary)' }}></div>
                <div className="color-info">
                  <p className="color-name">Text Secondary</p>
                  <p className="color-value">--color-text-secondary</p>
                  <p className="color-hex">#4C514F</p>
                </div>
              </div>
            </Col>
          </Row>

          <h3 className="subsection-title mb-3">Background & Component Colors</h3>
          <Row className="mb-4">
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-background)' }}></div>
                <div className="color-info">
                  <p className="color-name">Background</p>
                  <p className="color-value">--color-background</p>
                  <p className="color-hex">#F7F7F2</p>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-components-hover)' }}></div>
                <div className="color-info">
                  <p className="color-name">Components Hover</p>
                  <p className="color-value">--color-components-hover</p>
                  <p className="color-hex">#D6E5DE</p>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-components-inactive)' }}></div>
                <div className="color-info">
                  <p className="color-name">Components Inactive</p>
                  <p className="color-value">--color-components-inactive</p>
                  <p className="color-hex">#C9CECC</p>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3} className="mb-3">
              <div className="color-card">
                <div className="color-preview" style={{ backgroundColor: 'var(--color-components-stroke)' }}></div>
                <div className="color-info">
                  <p className="color-name">Components Stroke</p>
                  <p className="color-value">--color-components-stroke</p>
                  <p className="color-hex">#ADADAD</p>
                </div>
              </div>
            </Col>
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="section-title mb-4">Typography</h2>

          <h3 className="subsection-title mb-3">Font Families</h3>
          <Row className="mb-4">
            <Col md={12} className="mb-3">
              <Card className="typography-card">
                <Card.Body>
                  <p className="font-label">Inter (Primary)</p>
                  <p className="font-sample" style={{ fontFamily: 'var(--font-family-inter)' }}>
                    The quick brown fox jumps over the lazy dog
                  </p>
                  <code className="font-var">--font-family-inter</code>
                </Card.Body>
              </Card>
            </Col>
            <Col md={12} className="mb-3">
              <Card className="typography-card">
                <Card.Body>
                  <p className="font-label">IBM Plex Mono</p>
                  <p className="font-sample" style={{ fontFamily: 'var(--font-family-mono)' }}>
                    The quick brown fox jumps over the lazy dog
                  </p>
                  <code className="font-var">--font-family-mono</code>
                </Card.Body>
              </Card>
            </Col>
            <Col md={12} className="mb-3">
              <Card className="typography-card">
                <Card.Body>
                  <p className="font-label">FreightText Pro Bold</p>
                  <p className="font-sample" style={{ fontFamily: 'var(--font-family-freight)' }}>
                    The quick brown fox jumps over the lazy dog
                  </p>
                  <code className="font-var">--font-family-freight</code>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <h3 className="subsection-title mb-3">Type Scale</h3>
          <div className="type-scale">
            <div className="type-sample mb-3">
              <h1 style={{ fontFamily: 'var(--font-family-inter)' }}>Heading 1</h1>
              <code>font-size: 40px; font-weight: 600;</code>
            </div>
            <div className="type-sample mb-3">
              <h2>Heading 2</h2>
              <code>font-size: 16px; font-weight: 400;</code>
            </div>
            <div className="type-sample mb-3">
              <p className="lead">Lead Text</p>
              <code>font-size: 12px; text-transform: uppercase;</code>
            </div>
            <div className="type-sample mb-3">
              <p>Body Text</p>
              <code>font-size: 16px;</code>
            </div>
          </div>
        </section>

        <section className="mb-5">
          <h2 className="section-title mb-4">Buttons</h2>

          <Row className="mb-4">
            <Col xs={12}>
              <div className="button-samples">
                <Button className="btn-primary me-3 mb-2">Primary Button</Button>
                <Button className="btn-secondary me-3 mb-2">Secondary Button</Button>
                <Button className="btn-tertiary me-3 mb-2">Tertiary Button</Button>
                <Button className="btn-link mb-2">Link Button</Button>
              </div>
            </Col>
          </Row>

          <h3 className="subsection-title mb-3">Button States</h3>
          <Row>
            <Col md={6}>
              <Table className="button-states-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Default</td>
                    <td><Button className="btn-primary">Default</Button></td>
                  </tr>
                  <tr>
                    <td>Hover</td>
                    <td><Button className="btn-primary button-hover-demo">Hover</Button></td>
                  </tr>
                  <tr>
                    <td>Disabled</td>
                    <td><Button className="btn-primary" disabled>Disabled</Button></td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="section-title mb-4">Form Elements</h2>

          <Row>
            <Col md={6}>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Text Input</Form.Label>
                  <Form.Control type="text" placeholder="Enter text" />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Textarea</Form.Label>
                  <Form.Control as="textarea" rows={3} placeholder="Enter multiple lines of text" />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Select</Form.Label>
                  <Form.Select>
                    <option>Choose an option</option>
                    <option>Option 1</option>
                    <option>Option 2</option>
                    <option>Option 3</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check type="checkbox" label="Checkbox" />
                  <Form.Check type="radio" label="Radio Button" name="radioGroup" />
                  <Form.Check type="radio" label="Radio Button 2" name="radioGroup" />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="section-title mb-4">Cards</h2>

          <Row>
            <Col md={4} className="mb-3">
              <Card>
                <Card.Body>
                  <Card.Title>Card Title</Card.Title>
                  <Card.Text>
                    This is a standard card component with some example content to show layout and styling.
                  </Card.Text>
                  <Button className="btn-primary">Action</Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <div className="template-card p-4">
                <div className="text-center">
                  <div className="template-card-icon mb-3">📝</div>
                  <h3 className="template-card-title">Template Card</h3>
                  <p className="template-card-description">
                    A specialized card component for templates
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="section-title mb-4">Spacing & Layout</h2>

          <h3 className="subsection-title mb-3">Border Radius</h3>
          <Row className="mb-4">
            <Col xs={6} md={3} className="mb-3">
              <div className="spacing-demo">
                <div className="radius-box" style={{ borderRadius: 'var(--border-radius-primary)' }}>
                  Primary (32px)
                </div>
                <code className="mt-2 d-block">--border-radius-primary</code>
              </div>
            </Col>
            <Col xs={6} md={3} className="mb-3">
              <div className="spacing-demo">
                <div className="radius-box" style={{ borderRadius: 'var(--border-radius-secondary)' }}>
                  Secondary (8px)
                </div>
                <code className="mt-2 d-block">--border-radius-secondary</code>
              </div>
            </Col>
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="section-title mb-4">Alerts & Messages</h2>

          <Row>
            <Col md={12}>
              <Alert variant="success" className="mb-3">
                <strong>Success!</strong> This is a success alert message.
              </Alert>
              <Alert variant="warning" className="mb-3">
                <strong>Warning!</strong> This is a warning alert message.
              </Alert>
              <Alert variant="danger" className="mb-3">
                <strong>Error!</strong> This is an error alert message.
              </Alert>
              <Alert variant="info" className="mb-3">
                <strong>Info!</strong> This is an info alert message.
              </Alert>
            </Col>
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="section-title mb-4">Badges</h2>

          <div className="badge-samples">
            <Badge bg="primary" className="me-2">Primary</Badge>
            <Badge bg="secondary" className="me-2">Secondary</Badge>
            <Badge bg="success" className="me-2">Success</Badge>
            <Badge bg="danger" className="me-2">Danger</Badge>
            <Badge bg="warning" className="me-2">Warning</Badge>
            <Badge bg="info" className="me-2">Info</Badge>
            <Badge bg="light" text="dark" className="me-2">Light</Badge>
            <Badge bg="dark" className="me-2">Dark</Badge>
          </div>
        </section>

      </Container>
    </div>
  );
};

export default DesignSystem;